import { useCallback, useMemo, useState, useRef, useEffect } from 'react'
import type { JSX } from 'react'
import type * as React from 'react'
import { useMatch } from 'react-router-dom'

import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { clsx } from 'clsx'

import { useDebounce } from '@buffer-mono/popcorn'

import { Calendar as CalendarComponent } from '~publish/components/Calendar'
import { CalendarUndatedDraftDragOverlay } from '~publish/components/Calendar/CalendarUndatedDraftDragSource'
import { useCalendar } from '~publish/components/Calendar/hooks/useCalendar'
import { useCalendarMobileMode } from '~publish/components/Calendar/hooks/useCalendarMobileMode'
import type {
  CalendarItem,
  RenderItemFunction,
} from '~publish/components/Calendar/types'
import type { ContentItemDraft } from '~publish/components/ContentItemCard'
import { useAccount, useOrganizationId } from '~publish/contexts/AccountContext'
import { readFragment } from '~publish/graphql'
import { useChannelAgnosticDraftsEnabled } from '~publish/hooks/useChannelAgnosticDraftsEnabled'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { usePostDraggedTracking } from '~publish/hooks/usePostDraggedTracking'
import { useQueryParam } from '~publish/hooks/useQueryParam'
import { TrackRender } from '~publish/hooks/tracking'
import { useTimezone } from '~publish/hooks/useTimezone'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'
import { PostEntity } from '~publish/legacy/post/PostEntity'
import { CalendarPostCard_Post } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'
import type { CalendarPostCard_PostData } from '~publish/pages/Calendar/fragments/CalendarPostCard_Post'
import { mergePostsAndSlots } from '~publish/pages/Channel/QueueList/postSlotManager'
import type {
  SlotOrPost,
  Slot,
} from '~publish/pages/Channel/QueueList/postSlotManager'

import type { PostForCalendarHookResponse } from './hooks/useCalendarAndPostsList'
import {
  canShowMultiChannelCalendarSlots,
  useCalendarSlots,
} from './hooks/useCalendarSlots'
import type {
  CalendarSlot,
  CalendarSlotChannel,
} from './hooks/useCalendarSlots'
import { useHandleDragEnd } from './hooks/useHandleDragEnd'
import { useMoveToCustomTimeAlertDialog } from './hooks/useMoveToCustomTimeAlertDialog'
import { ContentItemCalendarItem } from './ContentItemCalendarItem'
import { useCalendarContentItemDrafts } from './useCalendarContentItemDrafts'
import { useCalendarAnalytics } from './hooks/useCalendarAnalytics'
import { PostItem } from './PostItem'
import { PostMiniature } from './PostMiniature'
import { MonthSlotItem, SlotItem, WeekSlotItem } from './SlotItem'
import {
  isUndatedDraftDragData,
  isUndatedDraftsSidebarDropData,
} from './undatedDraftDrag'
import { isUndatedContentItemDragData } from './contentItemDrag'
import { useUpdateContentItemTargetDate } from './hooks/useUpdateContentItemTargetDate'
import { newCalendarSingleChannel } from '~publish/legacy/routes'

import { PostingGoalSlotsTour } from '../../components/PostingGoalSlotsTour'

import styles from './CalendarPage.module.css'

const CTA_ADD_HOUR = 'publish-calendar-hour-addPost-1'
const CTA_ADD_DAY = 'publish-calendar-day-addPost-1'
const FIVE_MINUTES_MS = 5 * 60 * 1000

interface CalendarProps {
  channelCount: number
  channelsLoading: boolean
  children?: React.ReactNode
  className?: string
  dragBoundaryRef?: React.RefObject<HTMLElement>
  hasTwentyFourHourTimeFormat: boolean
  onboardingServices: CalendarSlotChannel['service'][] | null
  selectedChannelIds: string[] | void
  showLoadingProgress: boolean
  slotChannels: CalendarSlotChannel[]
  viewOnly: boolean
  postsQuery: PostForCalendarHookResponse
}

/**
 * Custom hook to keep track of the previous value of a variable across renders
 *
 * @template T - The type of the value to track
 * @param {T} value - The current value to track
 * @returns {T | undefined} - The previous value (undefined on first render)
 *
 * @example
 * // Track previous view mode to prevent tour flickering
 * const prevViewMode = usePreviousValue(viewMode)
 */
function usePreviousValue<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined)
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

export function Calendar({
  channelCount,
  channelsLoading,
  children,
  className,
  dragBoundaryRef,
  hasTwentyFourHourTimeFormat,
  onboardingServices,
  selectedChannelIds,
  showLoadingProgress,
  slotChannels,
  viewOnly,
  postsQuery,
}: CalendarProps): JSX.Element {
  const timezone = useTimezone()
  const dayToStartWeek = useWeekStartsOn()
  const weekStartsOn = dayToStartWeek === 'monday' ? 1 : 0
  const { viewMode, startDate, endDate } = useCalendar({
    timezone,
    weekStartsOn,
  })
  const isMobile = useCalendarMobileMode()
  const organizationId = useOrganizationId()
  useCalendarAnalytics({
    granularity: viewMode,
    channelsCount: channelCount,
    organizationId: organizationId ?? '',
    trackOpened: Boolean(organizationId) && !channelsLoading,
  })
  const { data, loading } = postsQuery
  const account = useAccount()

  // Channel-agnostic drafts with a target date in the visible range are placed
  // in cells alongside posts, behind the feature flag, and can be dragged to
  // reschedule their targetDate (see useHandleDragEnd).
  const channelAgnosticDraftsEnabled = useChannelAgnosticDraftsEnabled()
  const { drafts: contentItemDrafts } = useCalendarContentItemDrafts({
    start: startDate.toInstant().toString(),
    end: endDate.toInstant().toString(),
    skip: !channelAgnosticDraftsEnabled,
  })
  const updateContentItemTargetDate = useUpdateContentItemTargetDate()
  const [startTour, setStartTour] = useState(false)
  const debouncedStartTour = useDebounce(startTour, 400)
  const prevViewMode = usePreviousValue(viewMode) // [Hack]: Avoid tour flickering when changing view mode
  const isSingleChannel = selectedChannelIds?.length === 1

  const posts = useMemo(
    () => data?.posts.edges?.map((edge) => edge.node) || [],
    [data],
  )

  // Only count posts that are scheduled (i.e., not sent yet)
  const hasVisibleScheduledPosts = posts.some(
    (post) =>
      post.dueAt !== undefined &&
      post.dueAt !== null &&
      new Date(post.dueAt).getTime() >= Date.now(),
  )

  const showMultiChannelSlots =
    canShowMultiChannelCalendarSlots({
      channelsLoading,
      viewOnly,
    }) && selectedChannelIds?.length !== 1
  const showChannelDetails =
    showMultiChannelSlots || (isSingleChannel && !hasVisibleScheduledPosts)
  const slots = useCalendarSlots({
    selectedChannelIds,
    channels: slotChannels,
    onboardingServices,
    enableMultiChannelSlots: showMultiChannelSlots,
  })

  const items = useMemo(
    () => [
      ...buildItemsFromPostsAndSlots(
        mergePostsAndSlots({
          // Schedule slots are composer triggers, so a view-only calendar
          // renders none.
          slots: viewOnly ? [] : slots,
          posts: posts.map((post) => readFragment(CalendarPostCard_Post, post)),
        }),
        selectedChannelIds?.length === 1,
        viewOnly,
      ),
      ...buildItemsFromContentItemDrafts(contentItemDrafts, viewOnly),
    ],
    [posts, slots, selectedChannelIds, viewOnly, contentItemDrafts],
  )

  // The tour reads useSelectedChannel, which only has a ChannelProvider on the
  // single-channel route (@see CalendarPage SingleChannelProvider). The All
  // Channels route filtered to one channel makes selectedChannelIds length 1
  // but mounts no provider, so gate on the route, not the filter.
  const isSingleChannelRoute = !!useMatch(newCalendarSingleChannel.route)

  // A view-only calendar renders no slots, so the slots tour has no anchor —
  // and startTour may have latched true before viewOnly flipped.
  const showTour =
    isSingleChannelRoute &&
    !viewOnly &&
    debouncedStartTour &&
    !loading &&
    prevViewMode === viewMode

  const { triggerAttributes, createNewPostInComposer } = usePostComposer()
  const [selectedTagIds] = useQueryParam<string[]>('tags')

  const openComposer = useCallback(
    (timestamp: number): void => {
      const channels =
        selectedChannelIds === undefined || selectedChannelIds.length === 0
          ? []
          : selectedChannelIds

      const now = new Date().getTime()
      const scheduledAt = timestamp < now ? now + FIVE_MINUTES_MS : timestamp

      void createNewPostInComposer({
        cta: viewMode === 'week' ? CTA_ADD_HOUR : CTA_ADD_DAY,
        channels,
        prefillPostData: {
          dueAt: Math.round(scheduledAt / 1000),
          tags: selectedTagIds,
        },
      })
    },
    [createNewPostInComposer, selectedChannelIds, selectedTagIds, viewMode],
  )

  const firstVisibleSlot = useMemo(() => {
    return items.find((slot) => slot.type === 'slot')
  }, [items])
  const renderItem = useCallback<RenderItemFunction>(
    ({ id, timestamp, type, isDraggingOver }) => {
      if (type === 'slot' && selectedChannelIds) {
        const slot = slots.find((candidate) => candidate.id === id)
        if (!slot) return null

        const isFirstSlot = firstVisibleSlot?.id === id
        const channelService = showChannelDetails
          ? slot.channelService
          : undefined
        if (isMobile && viewMode === 'month') {
          if (isFirstSlot && isSingleChannel) setStartTour(true)
          return (
            <MonthSlotItem
              timestamp={timestamp}
              channelId={slot.channelId}
              channelName={slot.channelName}
              channelService={channelService}
              data-tour-id={isFirstSlot && isSingleChannel ? 'slot' : undefined}
              isDraggingOver={isDraggingOver}
              is24HourFormat={hasTwentyFourHourTimeFormat}
              isEmptyStateSlot={isSingleChannel && showChannelDetails}
              isValueLedEmptyStateSlot={showMultiChannelSlots}
            />
          )
        }
        if (isMobile && viewMode === 'week') {
          if (isFirstSlot && isSingleChannel) setStartTour(true)
          return (
            <WeekSlotItem
              timestamp={timestamp}
              channelId={slot.channelId}
              channelName={slot.channelName}
              channelService={channelService}
              data-tour-id={isFirstSlot && isSingleChannel ? 'slot' : undefined}
              isDraggingOver={isDraggingOver}
              is24HourFormat={hasTwentyFourHourTimeFormat}
              isEmptyStateSlot={isSingleChannel && showChannelDetails}
              isValueLedEmptyStateSlot={showMultiChannelSlots}
            />
          )
        }
        if (isFirstSlot && isSingleChannel) {
          setStartTour(true) // Start the tour after the first slot is rendered
          return (
            <SlotItem
              timestamp={timestamp}
              channelId={slot.channelId}
              channelName={slot.channelName}
              channelService={channelService}
              data-tour-id="slot"
              isDraggingOver={isDraggingOver}
              is24HourFormat={hasTwentyFourHourTimeFormat}
              isEmptyStateSlot={showChannelDetails}
            />
          )
        }
        return (
          <SlotItem
            timestamp={timestamp}
            channelId={slot.channelId}
            channelName={slot.channelName}
            channelService={channelService}
            isDraggingOver={isDraggingOver}
            is24HourFormat={hasTwentyFourHourTimeFormat}
            isEmptyStateSlot={isSingleChannel && showChannelDetails}
            isValueLedEmptyStateSlot={showMultiChannelSlots}
          />
        )
      }
      if (type === 'post') {
        const postIndex = posts.findIndex((post) => post?.id === id)
        const post = posts[postIndex]
        if (!post) return null
        return (
          <PostItem
            post={post}
            timestamp={timestamp}
            viewMode={viewMode}
            timezone={timezone}
            isDraggingOver={isDraggingOver}
          />
        )
      }
      if (type === 'contentItem') {
        const draft = contentItemDrafts.find((item) => item.id === id)
        if (!draft) return null
        return (
          <ContentItemCalendarItem
            contentItem={draft}
            condensed={viewMode === 'month'}
          />
        )
      }
      return null
    },
    [
      firstVisibleSlot?.id,
      hasTwentyFourHourTimeFormat,
      posts,
      contentItemDrafts,
      isMobile,
      isSingleChannel,
      selectedChannelIds,
      showChannelDetails,
      showMultiChannelSlots,
      slots,
      timezone,
      viewMode,
    ],
  )

  // Track post drag events
  const trackPostDrag = usePostDraggedTracking()
  const handleDragStart = useCallback(
    (event: DragStartEvent): void => {
      if (!event.active.id) return
      // Channel-less drafts aren't posts; skip the post-drag analytics.
      if (event.active.data.current?.type === 'contentItem') return
      if (isUndatedDraftDragData(event.active.data.current)) {
        trackPostDrag(event.active.data.current.post)
        return
      }

      const postRaw = posts?.find((p) => p.id === event.active.id)
      const post = readFragment(CalendarPostCard_Post, postRaw)
      if (post) trackPostDrag(post)
    },
    [posts, trackPostDrag],
  )
  const [showDialog, Dialog] = useMoveToCustomTimeAlertDialog()

  const handleDragEnd = useHandleDragEnd({
    posts:
      data?.posts.edges?.map((edge) =>
        readFragment(CalendarPostCard_Post, edge.node),
      ) ?? [],
    preserveTime: viewMode === 'month',
    timezone,
    is24HourFormat: hasTwentyFourHourTimeFormat,
    contentItemDrafts,
    updateContentItemTargetDate,
    onMovingToCustomSchedule: async (
      previousDueAt,
      newDueAt,
    ): Promise<boolean> => {
      if (!previousDueAt) return true

      const result = await showDialog({
        fromDate: previousDueAt,
        toDate: newDueAt,
      })
      return result === 'confirmed' // continue with the move to custom time
    },
  })

  const addItem = useMemo(
    () => ({
      ...triggerAttributes,
      onClick: openComposer,
    }),
    [triggerAttributes, openComposer],
  )

  const renderDragOverlay = useCallback(
    (active: DragStartEvent['active']): React.ReactNode => {
      if (isUndatedContentItemDragData(active.data.current)) {
        return (
          <CalendarUndatedDraftDragOverlay>
            <ContentItemCalendarItem
              contentItem={active.data.current.contentItem}
              condensed
            />
          </CalendarUndatedDraftDragOverlay>
        )
      }
      if (!isUndatedDraftDragData(active.data.current)) return null

      return (
        <CalendarUndatedDraftDragOverlay>
          <PostMiniature
            post={active.data.current.post}
            className={styles.undatedDraftMiniature}
          />
        </CalendarUndatedDraftDragOverlay>
      )
    },
    [],
  )
  const shouldWaitForDragEnd = useCallback((event: DragEndEvent): boolean => {
    return isUndatedDraftsSidebarDropData(event.over?.data?.current)
  }, [])

  return (
    <CalendarComponent
      className={className}
      headerProgress={
        <div className={styles.calendarLoadingBar} aria-hidden="true">
          <span
            className={clsx(
              styles.calendarLoadingBarProgress,
              showLoadingProgress && styles.calendarLoadingBarProgressVisible,
            )}
          />
        </div>
      }
      calendarSiblings={
        <>
          {showMultiChannelSlots && slots.length > 0 && !loading && (
            <TrackRender
              componentName="ValueLedCalendarEmptyState"
              organizationId={organizationId}
              product="publish"
              platform="new_publish"
            />
          )}
          {Dialog}
          {showTour && <PostingGoalSlotsTour />}
        </>
      }
      dragBoundaryRef={dragBoundaryRef}
      timezone={timezone}
      weekStartsOn={weekStartsOn}
      is24HourFormat={hasTwentyFourHourTimeFormat}
      readOnly={viewOnly}
      loading={loading}
      preferencesLoading={account.loading}
      items={items}
      renderItem={renderItem}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      shouldWaitForDragEnd={shouldWaitForDragEnd}
      renderDragOverlay={renderDragOverlay}
      addItemProps={addItem}
    >
      {children}
    </CalendarComponent>
  )
}

function isSlot(entry: SlotOrPost<CalendarPostCard_PostData>): entry is Slot {
  return 'date' in entry && !('__typename' in entry)
}

function isCalendarSlot(
  entry: SlotOrPost<CalendarPostCard_PostData>,
): entry is CalendarSlot {
  return (
    isSlot(entry) &&
    'id' in entry &&
    'channelId' in entry &&
    'channelService' in entry
  )
}

function isPost(
  entry: SlotOrPost<CalendarPostCard_PostData>,
): entry is CalendarPostCard_PostData & { dueAt: string } {
  return (
    '__typename' in entry && entry.__typename === 'Post' && entry.dueAt != null
  )
}

function buildItemsFromPostsAndSlots(
  entries: SlotOrPost<CalendarPostCard_PostData>[],
  isSingleChannel = false,
  viewOnly = false,
): CalendarItem[] {
  return entries.flatMap<CalendarItem>((entry) => {
    if (isPost(entry)) {
      return [
        {
          id: entry.id,
          timestamp: new Date(entry.dueAt).getTime(),
          type: 'post',
          draggable:
            !viewOnly &&
            !PostEntity.isSent(entry) &&
            entry.status !== 'error' &&
            entry.status !== 'sending',
          droppable:
            !viewOnly &&
            !PostEntity.isSent(entry) &&
            entry.status !== 'error' &&
            entry.status !== 'sending' &&
            isSingleChannel,
        },
      ]
    }
    if (isCalendarSlot(entry)) {
      return [
        {
          id: entry.id,
          timestamp: new Date(entry.date).getTime(),
          type: 'slot',
          draggable: false,
          droppable: !viewOnly,
        },
      ]
    }
    return []
  })
}

function buildItemsFromContentItemDrafts(
  drafts: ContentItemDraft[],
  viewOnly = false,
): CalendarItem[] {
  return drafts
    .filter((draft): draft is ContentItemDraft & { targetDate: string } =>
      Boolean(draft.targetDate),
    )
    .map((draft) => ({
      id: draft.id,
      timestamp: new Date(draft.targetDate).getTime(),
      type: 'contentItem',
      // Draggable to reschedule its targetDate; never a drop target (drafts
      // don't swap or accept other items).
      draggable: !viewOnly,
      droppable: false,
    }))
}
